import type { HostShare, ShareResult } from '@puzzlemania/core';

/**
 * Module 79 (WP-79.8) — the one share implementation, for every profile.
 *
 * Order: the system share sheet (`navigator.share`), then the clipboard, then
 * `manual` (the game shows the text for the player to copy). **Never
 * `window.open`**: the Playgama frame is sandboxed without `allow-popups`, so a
 * "share to WhatsApp" URL opens nothing there. Its `allow` list does grant
 * `web-share` and `clipboard-write`, which is why this order works inside it.
 *
 * A share sheet the player closes rejects with `AbortError` → `cancelled`.
 * Any other rejection (`NotAllowedError` when the tap's activation was lost, a
 * policy block) falls through to the clipboard rather than failing the tap.
 */
export interface ShareEnv {
  share?: ((data: { text: string }) => Promise<void>) | undefined;
  writeText?: ((text: string) => Promise<void>) | undefined;
}

function browserEnv(): ShareEnv {
  if (typeof navigator === 'undefined') return {};
  return {
    share: typeof navigator.share === 'function' ? (d) => navigator.share(d) : undefined,
    writeText: navigator.clipboard?.writeText ? (t) => navigator.clipboard.writeText(t) : undefined,
  };
}

export function createHostShare(
  config: { productName: string; links: Readonly<Record<string, string>> },
  /** Which link applies, asked at share time (a host knows its platform only after init). */
  resolveLink: (links: Readonly<Record<string, string>>) => string | null,
  env: () => ShareEnv = browserEnv,
): HostShare {
  return {
    productName: config.productName,
    async share(body: string): Promise<ShareResult> {
      const link = resolveLink(config.links);
      const text = link ? `${body}\n${link}` : body;
      const { share, writeText } = env();
      if (share) {
        try {
          await share({ text });
          return { outcome: 'shared', text };
        } catch (error) {
          if ((error as { name?: string } | null)?.name === 'AbortError') {
            return { outcome: 'cancelled', text };
          }
        }
      }
      if (writeText) {
        try {
          await writeText(text);
          return { outcome: 'copied', text };
        } catch {
          // Clipboard refused (permissions, an insecure context): show it instead.
        }
      }
      return { outcome: 'manual', text };
    },
  };
}
